/**
 * Shadow Utilities
 *
 * Platform-aware shadow definitions for iOS, Android, and Web.
 * Eliminates the need for duplicate shadow declarations (116 instances found).
 *
 * Elevation levels match Material Design guidelines:
 * - xs: Subtle depth (search bars, inputs)
 * - sm: Small elevation (buttons)
 * - md: Medium elevation (cards, dropdowns)
 * - lg: High elevation (modals, dialogs)
 * - xl: Maximum elevation (important overlays)
 * - fab: Special elevation for floating action buttons
 */

import { Platform, ViewStyle } from 'react-native';
import { Colors } from './colors';

/**
 * Consistent shadow definitions for all platforms
 * Use with spread operator: ...Shadows.md
 */
export const Shadows = {
  none: {
    ...Platform.select({
      ios: {
        shadowColor: Colors.shadow,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0,
        shadowRadius: 0,
      },
      android: {
        elevation: 0,
      },
      web: {
        boxShadow: 'none',
      },
    }),
  },

  xs: {
    ...Platform.select({
      ios: {
        shadowColor: Colors.shadow,
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.08,
        shadowRadius: 3,
      },
      android: {
        elevation: 2,
      },
      web: {
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
      },
    }),
  },

  sm: {
    ...Platform.select({
      ios: {
        shadowColor: Colors.shadow,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 4,
      },
      web: {
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      },
    }),
  },

  md: {
    ...Platform.select({
      ios: {
        shadowColor: Colors.shadow,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 8,
      },
      android: {
        elevation: 6,
      },
      web: {
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
      },
    }),
  },

  lg: {
    ...Platform.select({
      ios: {
        shadowColor: Colors.shadow,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: 12,
      },
      android: {
        elevation: 8,
      },
      web: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
      },
    }),
  },

  xl: {
    ...Platform.select({
      ios: {
        shadowColor: Colors.shadow,
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.25,
        shadowRadius: 16,
      },
      android: {
        elevation: 12,
      },
      web: {
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.25)',
      },
    }),
  },

  // Special shadows for specific components
  fab: {
    ...Platform.select({
      ios: {
        shadowColor: Colors.shadow,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
      },
      android: {
        elevation: 5,
      },
      web: {
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.25)',
      },
    }),
  },

  filterCard: {
    ...Platform.select({
      ios: {
        shadowColor: Colors.shadow,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 12,
      },
      android: {
        elevation: 6,
      },
      web: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
    }),
  },

  // ── Polish round: soft green-tinted elevation ──────────────────────────────

  /** Hairline cards: barely-there Bosgroen shadow */
  card: {
    ...Platform.select({
      ios: {
        shadowColor: Colors.shadow,
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.05,
        shadowRadius: 18,
      },
      android: {
        elevation: 2,
      },
      web: {
        boxShadow: '0 1px 2px rgba(30, 59, 49, 0.04), 0 6px 18px rgba(30, 59, 49, 0.05)',
      },
    }),
  },

  /** Card on hover (web): lifts and deepens */
  cardHover: {
    ...Platform.select({
      ios: {
        shadowColor: Colors.shadow,
        shadowOffset: { width: 0, height: 14 },
        shadowOpacity: 0.1,
        shadowRadius: 30,
      },
      android: {
        elevation: 6,
      },
      web: {
        boxShadow: '0 2px 4px rgba(30, 59, 49, 0.06), 0 14px 30px rgba(30, 59, 49, 0.10)',
      },
    }),
  },

  /** Dialogs, modals and menus float on a deeper green shadow */
  panel: {
    ...Platform.select({
      ios: {
        shadowColor: Colors.shadow,
        shadowOffset: { width: 0, height: 12 },
        shadowOpacity: 0.16,
        shadowRadius: 32,
      },
      android: {
        elevation: 10,
      },
      web: {
        boxShadow: '0 12px 32px rgba(30, 59, 49, 0.16)',
      },
    }),
  },

  /** Floating "+" button: green shadow (design v2) */
  fabSoft: {
    ...Platform.select({
      ios: {
        shadowColor: Colors.shadow,
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.18,
        shadowRadius: 16,
      },
      android: {
        elevation: 8,
      },
      web: {
        boxShadow: '0 6px 16px rgba(30, 59, 49, 0.18)',
      },
    }),
  },

  /** Focused input / select / composer (web): accent ring next to the accent border */
  focusRing: {
    ...Platform.select({
      ios: {
        shadowColor: Colors.primary,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 3,
      },
      android: {
        elevation: 0,
      },
      web: {
        boxShadow: '0 0 0 3px rgba(79, 109, 122, 0.30)',
      },
    }),
  },

  /** @deprecated design v2: the mobile header draws a 1px borderBottom instead */
  header: {
    ...Platform.select({
      ios: {
        shadowColor: Colors.shadow,
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.08,
        shadowRadius: 0,
      },
      android: {
        elevation: 1,
      },
      web: {
        boxShadow: '0 1px 0 rgba(30, 59, 49, 0.08)',
      },
    }),
  },
} as const satisfies Record<string, ViewStyle>;

/**
 * Type for shadow keys
 */
export type ShadowKey = keyof typeof Shadows;
